import React, { useState } from "react";
import { useIntl } from "react-intl";
import { Modal, ModalBody } from "reactstrap";
import { useMeCore } from "../../../cores/me";
import { WebsiteIcon } from "./WebsiteIcon";
import "./AiGenerateModal.scss";
import "./website-builder.scss";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onGenerate: (data: { prompt: string; companyName: string; industry: string }) => Promise<void>;
  isLoading?: boolean;
}

export function AiGenerateModal({ isOpen, onClose, onGenerate, isLoading }: Props) {
  const intl = useIntl();
  const { company } = useMeCore();
  const [prompt, setPrompt] = useState("");
  const [companyName, setCompanyName] = useState(company?.name || "");
  const [industry, setIndustry] = useState("");

  const handleSubmit = async () => {
    try {
      await onGenerate({ prompt, companyName, industry });
      onClose();
    } catch {
      // Error toast handled in core
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} className="wp-ai-modal" size="lg" centered>
      <div className="wp-ai-modal__header">
        <div className="wp-ai-modal__header-icon">
          <WebsiteIcon icon="wand-magic-sparkles" />
        </div>
        <div>
          <h2>{intl.formatMessage({ id: "app.websitePage.ai.title" })}</h2>
          <p>{intl.formatMessage({ id: "app.websitePage.ai.creditsNotice" })}</p>
        </div>
        <button type="button" className="wp-ai-modal__close" onClick={onClose}>
          <WebsiteIcon icon="times" />
        </button>
      </div>
      <ModalBody className="wp-ai-modal__body">
        <div className="wp-ai-modal__grid">
          <div className="wp-ai-modal__field">
            <label>{intl.formatMessage({ id: "app.websitePage.ai.companyName" })}</label>
            <input className="wp-input" value={companyName} onChange={(e) => setCompanyName(e.target.value)} />
          </div>
          <div className="wp-ai-modal__field">
            <label>{intl.formatMessage({ id: "app.websitePage.ai.industry" })}</label>
            <input
              className="wp-input"
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
              placeholder={intl.formatMessage({ id: "app.websitePage.ai.industryPlaceholder" })}
            />
          </div>
        </div>
        <div className="wp-ai-modal__field">
          <label>{intl.formatMessage({ id: "app.websitePage.ai.prompt" })}</label>
          <textarea
            className="wp-input wp-ai-modal__textarea"
            rows={5}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder={intl.formatMessage({ id: "app.websitePage.ai.promptPlaceholder" })}
          />
        </div>
      </ModalBody>
      <div className="wp-ai-modal__footer">
        <button type="button" className="wp-btn wp-btn--secondary" onClick={onClose} disabled={isLoading}>
          {intl.formatMessage({ id: "app.websitePage.cancel" })}
        </button>
        <button
          type="button"
          className="wp-btn wp-btn--primary"
          onClick={handleSubmit}
          disabled={isLoading || !prompt.trim()}
        >
          <WebsiteIcon icon="magic" />
          {isLoading
            ? intl.formatMessage({ id: "app.websitePage.ai.generating" })
            : intl.formatMessage({ id: "app.websitePage.ai.generate" })}
        </button>
      </div>
    </Modal>
  );
}
